import { useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useMonthCursor } from '@/hooks/useMonthCursor'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Pencil, Copy, Trash2, ChevronLeft, ChevronRight, ArrowLeft, Repeat, ArrowLeftRight, Receipt } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { MonthPicker } from '@/components/ui/month-picker'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { TableSkeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SortHead, useTableSort, cmp } from '@/components/ui/sortable-head'
import { useAccounts } from '@/hooks/useAccounts'
import { useTransactions, useDeleteTransaction, useToggleStatus, type TransactionRow } from '@/hooks/useTransactions'
import { useHousehold } from '@/context/HouseholdContext'
import { useConfirm } from '@/context/ConfirmProvider'
import { useT } from '@/i18n/I18nProvider'
import { useCurrentRates, amountInAccount } from '@/hooks/useCurrentRates'
import { formatCurrency } from '@/lib/currency'
import { accountIcon } from '@/lib/accountIcon'
import { TagBadges } from '@/components/transactions/TagBadges'
import { TransactionFormDialog, defaultDateForMonth } from '@/components/transactions/TransactionFormDialog'
import { TransferDialog } from '@/components/transactions/TransferDialog'
import { IncludeRecurringDialog } from '@/components/recurring/IncludeRecurringDialog'
import { useTranslateCategory } from '@/lib/categoryDisplay'

function monthLabel(d: Date) {
  return format(d, "MMMM 'de' yyyy", { locale: ptBR })
}
function isCurrentMonth(d: Date) {
  const n = new Date()
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth()
}
function bounds(d: Date) {
  const first = new Date(d.getFullYear(), d.getMonth(), 1)
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  const iso = (x: Date) => x.toISOString().slice(0, 10)
  return { from: iso(first), to: iso(last) }
}

export function AccountStatementPage() {
  const { t: tr } = useT()
  const tc = useTranslateCategory()
  const { id = '' } = useParams()
  const { baseCurrency } = useHousehold()
  const { data: accounts } = useAccounts(true)
  const del = useDeleteTransaction()
  const toggle = useToggleStatus()
  const confirm = useConfirm()

  const account = useMemo(() => (accounts ?? []).find((a) => a.id === id), [accounts, id])
  const { data: allTxs, isLoading } = useTransactions({ accountId: id })
  const { data: rates } = useCurrentRates()
  const cur = account?.currency ?? baseCurrency

  // Efeito de um lançamento PAGO no saldo, na moeda da conta.
  const toCur = (t: TransactionRow) => amountInAccount(t, cur, rates)
  const signedPaid = (t: TransactionRow): number => {
    if (t.status !== 'paid') return 0
    if (t.kind === 'income') return toCur(t)
    if (t.kind === 'expense') return -toCur(t)
    return 0
  }

  const [cursor, setCursor] = useMonthCursor()
  const range = useMemo(() => bounds(cursor), [cursor])

  const [dialogOpen, setDialogOpen] = useState(false)
  const [includeOpen, setIncludeOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)
  const [editing, setEditing] = useState<TransactionRow | null>(null)
  const [duplicating, setDuplicating] = useState<TransactionRow | null>(null)

  // Saldo atual + saldo anterior (fim do mês passado) + movimentação do mês.
  // Saldo do fim = Saldo anterior + Entradas − Saídas (saldo corrido).
  const { currentBalance, startBalance, endBalance, monthTxs, entradas, saidas, transfIn, transfOut } = useMemo(() => {
    const init = Number(account?.initial_balance ?? 0)
    let current = init
    let start = init
    const monthTxs: TransactionRow[] = []
    let entradas = 0
    let saidas = 0
    let transfIn = 0
    let transfOut = 0
    for (const t of allTxs ?? []) {
      const s = signedPaid(t)
      current += s
      if (t.date < range.from) start += s
      else if (t.date <= range.to) {
        monthTxs.push(t)
        if (t.status === 'paid') {
          const isTransfer = !!t.transfer_group_id
          const v = toCur(t)
          if (t.kind === 'income') isTransfer ? (transfIn += v) : (entradas += v)
          else if (t.kind === 'expense') isTransfer ? (transfOut += v) : (saidas += v)
        }
      }
    }
    monthTxs.sort((a, b) => a.date.localeCompare(b.date) || a.created_at.localeCompare(b.created_at))
    const endBalance = start + entradas - saidas + transfIn - transfOut
    return { currentBalance: current, startBalance: start, endBalance, monthTxs, entradas, saidas, transfIn, transfOut }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allTxs, account, range, rates])

  // O saldo é CORRIDO: só faz sentido em ordem cronológica. Calculamos o saldo
  // de cada linha por data ANTES de ordenar a exibição — assim, ao ordenar por
  // descrição ou valor, cada linha continua mostrando o saldo certo dela.
  type SKey = 'date' | 'desc' | 'cat' | 'value'
  const { sort, toggle: toggleSort } = useTableSort<SKey>('date', 'desc', ['value'])
  const rows = useMemo(() => {
    let running = startBalance
    const withBalance = monthTxs.map((t) => {
      running += signedPaid(t)
      return { t, saldo: running }
    })
    const val = (r: { t: TransactionRow; saldo: number }): string | number => {
      switch (sort.key) {
        case 'date':
          return r.t.date + r.t.created_at
        case 'desc':
          return (r.t.description ?? '').toLowerCase()
        case 'cat':
          return (r.t.category?.name ?? '').toLowerCase()
        case 'value':
          return (r.t.kind === 'expense' ? -1 : 1) * Number(r.t.amount)
      }
    }
    return withBalance.sort((a, b) => cmp(val(a), val(b), sort.dir))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthTxs, startBalance, sort, rates])

  function shiftMonth(delta: number) {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1))
  }
  function openNew() {
    setEditing(null)
    setDuplicating(null)
    setDialogOpen(true)
  }
  function openEdit(t: TransactionRow) {
    setEditing(t)
    setDuplicating(null)
    setDialogOpen(true)
  }
  function openDuplicate(t: TransactionRow) {
    setEditing(null)
    setDuplicating(t)
    setDialogOpen(true)
  }
  async function remove(t: TransactionRow) {
    const isTransfer = Boolean(t.transfer_group_id)
    const ok = await confirm({
      title: isTransfer ? tr('acct.deleteTransferTitle') : tr('acct.deleteTxTitle'),
      description: isTransfer ? tr('acct.deleteTransferDesc') : tr('acct.cannotUndo'),
      confirmText: tr('common.delete'),
      tone: 'destructive',
    })
    if (!ok) return
    try {
      await del.mutateAsync({ id: t.id, transferGroupId: t.transfer_group_id })
      toast.success(isTransfer ? tr('acct.transferDeleted') : tr('acct.txDeleted'))
    } catch (e) {
      toast.error(tr('acct.deleteTxError'), { description: (e as Error).message })
    }
  }
  async function onToggle(t: TransactionRow, checked: boolean) {
    try {
      await toggle.mutateAsync({ id: t.id, status: checked ? 'paid' : 'pending' })
      toast.success(checked ? tr('acct.markedPaid') : tr('acct.markedPending'))
    } catch (e) {
      toast.error(tr('acct.statusError'), { description: (e as Error).message })
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <Link to="/contas" className="text-muted-foreground hover:text-foreground mb-1 inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="size-4" /> {tr('nav.accounts')}
        </Link>
        <PageHeader
          title={account ? `${accountIcon(account)} ${account.name}` : tr('acct.accountFallback')}
          subtitle={
            <>
              {tr('acct.currentBalance')}{' '}
              <span className={`tabular-nums font-medium ${currentBalance < 0 ? 'text-destructive' : ''}`}>
                {formatCurrency(currentBalance, cur)}
              </span>
            </>
          }
          actions={
            <>
              <Button variant="outline" onClick={() => setTransferOpen(true)}>
                <ArrowLeftRight className="size-4" /> {tr('acct.transfer')}
              </Button>
              <Button variant="outline" onClick={() => setIncludeOpen(true)}>
                <Repeat className="size-4" /> {tr('acct.includeRecurring')}
              </Button>
              <Button onClick={openNew}>
                <Plus className="size-4" /> {tr('acct.newTransaction')}
              </Button>
            </>
          }
        />
      </div>

      {/* Navegador de mês + movimentação (entradas/saídas/resultado) */}
      <div className="flex items-center justify-between rounded-lg border p-2">
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} aria-label={tr('acct.prevMonth')}>
          <ChevronLeft className="size-5" />
        </Button>
        <div className="text-center">
          <MonthPicker value={cursor} onChange={setCursor} />
          <div className="text-muted-foreground text-xs">
            {tr('acct.prevBalance')} <span className="tabular-nums">{formatCurrency(startBalance, cur)}</span> + {tr('acct.income')}{' '}
            <span className="text-success tabular-nums">{formatCurrency(entradas, cur)}</span> − {tr('acct.expenses')}{' '}
            <span className="text-destructive tabular-nums">{formatCurrency(saidas, cur)}</span>
            {(transfIn > 0 || transfOut > 0) && (
              <>
                {' '}
                {transfOut > 0 && (
                  <>
                    − {tr('acct.transferOut')} <span className="tabular-nums">{formatCurrency(transfOut, cur)}</span>
                  </>
                )}
                {transfIn > 0 && (
                  <>
                    {' '}+ {tr('acct.transferIn')} <span className="tabular-nums">{formatCurrency(transfIn, cur)}</span>
                  </>
                )}
              </>
            )}{' '}
            ={' '}
            <span className={`tabular-nums font-medium ${endBalance < 0 ? 'text-destructive' : ''}`}>
              {formatCurrency(endBalance, cur)}
            </span>
          </div>
          {!isCurrentMonth(cursor) && (
            <button className="text-primary text-xs hover:underline" onClick={() => setCursor(new Date())}>
              {tr('acct.backToCurrentMonth')}
            </button>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} aria-label={tr('acct.nextMonth')}>
          <ChevronRight className="size-5" />
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <TableSkeleton rows={6} cols={7} />
          ) : monthTxs.length > 0 ? (
            <>
            <div className="hidden overflow-x-auto md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" title={tr('acct.paidQ')}>✓</TableHead>
                    <SortHead label={tr('acct.colDate')} k="date" sort={sort} onToggle={toggleSort} />
                    <SortHead label={tr('acct.colDesc')} k="desc" sort={sort} onToggle={toggleSort} />
                    <SortHead label={tr('acct.colCategory')} k="cat" sort={sort} onToggle={toggleSort} />
                    <SortHead label={tr('acct.colValue')} k="value" sort={sort} onToggle={toggleSort} className="text-right" />
                    <TableHead className="text-right">{tr('acct.colBalance')}</TableHead>
                    <TableHead className="w-20" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(({ t, saldo: running }) => {
                    const isIncome = t.kind === 'income'
                    return (
                      <TableRow key={t.id} className={t.status === 'pending' ? 'opacity-70' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={t.status === 'paid'}
                            onCheckedChange={(v) => onToggle(t, v === true)}
                            aria-label={t.status === 'paid' ? tr('acct.markPending') : tr('acct.markPaid')}
                          />
                        </TableCell>
                        <TableCell className="whitespace-nowrap tabular-nums">
                          {format(new Date(t.date + 'T00:00:00'), 'dd/MM/yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            {t.transfer_group_id && <ArrowLeftRight className="text-muted-foreground size-3 shrink-0" />}
                            {t.recurring_id && <Repeat className="text-muted-foreground size-3 shrink-0" />}
                            <span className="max-w-[200px] truncate">
                              {t.description || <span className="text-muted-foreground">—</span>}
                            </span>
                            <span className="flex shrink-0 items-center gap-1">
                              {t.status === 'pending' && <Badge variant="outline">{tr('acct.pending')}</Badge>}
                              <TagBadges tags={t.tags} />
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{tc(t.category?.name) || '—'}</TableCell>
                        <TableCell className={`text-right tabular-nums whitespace-nowrap ${isIncome ? 'text-success' : ''}`}>
                          {t.kind === 'expense' ? '-' : t.kind === 'income' ? '+' : ''}
                          {formatCurrency(Number(t.amount), t.currency)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums whitespace-nowrap">
                          {t.status === 'paid' ? (
                            formatCurrency(running, cur)
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(t)} aria-label={tr('common.edit')}>
                              <Pencil className="size-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openDuplicate(t)} aria-label={tr('acct.duplicate')}>
                              <Copy className="size-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => remove(t)} aria-label={tr('common.delete')}>
                              <Trash2 className="text-destructive size-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-2 p-3 md:hidden">
              {rows.map(({ t, saldo: running }) => {
                const isIncome = t.kind === 'income'
                return (
                  <div key={t.id} className={`rounded-xl border p-3 ${t.status === 'pending' ? 'opacity-70' : ''}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-1.5">
                        {t.transfer_group_id && <ArrowLeftRight className="text-muted-foreground size-3 shrink-0" />}
                        {t.recurring_id && <Repeat className="text-muted-foreground size-3 shrink-0" />}
                        <span className="truncate font-medium">
                          {t.description || <span className="text-muted-foreground">—</span>}
                        </span>
                        <span className="flex shrink-0 items-center gap-1">
                          {t.status === 'pending' && <Badge variant="outline">{tr('acct.pending')}</Badge>}
                          <TagBadges tags={t.tags} />
                        </span>
                      </div>
                      <span
                        className={`shrink-0 text-right font-bold tabular-nums whitespace-nowrap ${isIncome ? 'text-success' : ''}`}
                      >
                        {t.kind === 'expense' ? '-' : t.kind === 'income' ? '+' : ''}
                        {formatCurrency(Number(t.amount), t.currency)}
                      </span>
                    </div>
                    <div className="text-muted-foreground mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs">
                      <span className="tabular-nums">{format(new Date(t.date + 'T00:00:00'), 'dd/MM/yyyy')}</span>
                      <span>{tc(t.category?.name) || '—'}</span>
                      <span>
                        {tr('acct.colBalance')}:{' '}
                        {t.status === 'paid' ? (
                          <span className="tabular-nums">{formatCurrency(running, cur)}</span>
                        ) : (
                          '—'
                        )}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={t.status === 'paid'}
                          onCheckedChange={(v) => onToggle(t, v === true)}
                          aria-label={t.status === 'paid' ? tr('acct.markPending') : tr('acct.markPaid')}
                        />
                        <span className="text-muted-foreground text-xs">{t.status === 'paid' ? tr('acct.paid') : tr('acct.pending')}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(t)} aria-label={tr('common.edit')}>
                          <Pencil className="size-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openDuplicate(t)} aria-label={tr('acct.duplicate')}>
                          <Copy className="size-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(t)} aria-label={tr('common.delete')}>
                          <Trash2 className="text-destructive size-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            </>
          ) : (
            <EmptyState
              icon={Receipt}
              title={tr('acct.emptyMonth', { month: monthLabel(cursor) })}
              action={
                <Button onClick={openNew}>
                  <Plus className="size-4" /> {tr('acct.newTransaction')}
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      <TransactionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        transaction={editing}
        duplicateFrom={duplicating}
        defaultAccountId={id}
        defaultDate={defaultDateForMonth(cursor)}
      />
      <IncludeRecurringDialog
        open={includeOpen}
        onOpenChange={setIncludeOpen}
        year={cursor.getFullYear()}
        month0={cursor.getMonth()}
        accountId={id}
        title={tr('acct.includeRecurringTitle', { name: account?.name ?? tr('acct.accountFallback') })}
      />
      <TransferDialog
        open={transferOpen}
        onOpenChange={setTransferOpen}
        defaultFromId={id}
        defaultDate={defaultDateForMonth(cursor)}
        title={tr('acct.transferTitle', { name: account?.name ?? tr('acct.accountFallback') })}
      />
    </div>
  )
}
